/**
 * Goethe-Zertifikat B1 blog post: Redemittel Schreiben & Sprechen (PDF).
 * Content: intro, Goethe B1 redemittel, B1 Schreiben/Speaking, practising under exam conditions, FAQ.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'Where can I access the B1 phrases list?',
    answer:
      'On this page. The download link above gives you the complete Goethe B1 list as a free PDF, with Schreiben and Sprechen phrases, English meanings and example sentences. No account is needed.',
  },
  {
    question: 'Does Logami help with the Goethe B1 speaking exam?',
    answer:
      'The Goethe B1 mock exams include the speaking tasks, so you can see exactly what you will be asked and prepare your answers, and the Redemittel trainer drills the speaking phrases. Logami does not assess spoken answers, so practise saying them out loud, ideally with a partner, as you will in the exam.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const GoetheB1RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        Redemittel are the fixed phrases that carry an exam answer: how you start an email, give your opinion or make a suggestion. In the <strong>Goethe-Zertifikat B1</strong> they count in two modules: <strong>Schreiben</strong>, three tasks in 60 minutes, and <strong>Sprechen</strong>, about 15 minutes with a partner. Below are the <strong>goethe b1 redemittel</strong> sorted by task, with English meanings and examples, and the full list is a free PDF.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How Do You Apply Goethe B1 Redemittel?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        To succeed in the <strong>b1 exam</strong>, candidates must deploy specific transitional phrases to connect ideas. Utilizing these ready-to-use phrases demonstrates foundational syntactic control to the examiners, ensuring you secure maximum points for coherence in the Goethe B1 <strong>Schreiben</strong> and <strong>Sprechen</strong> modules.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The exam demands structural consistency. Instead of struggling with basic conjunctions, test takers should have a small set of ready-to-use connectors to argue, hypothesize, and conclude effectively. Having a reliable list ensures you memorize the exact phrases your graders are listening for.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Executing the B1 Schreiben and Speaking Exam
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Executing a successful presentation requires immediate, spontaneous <strong>german language</strong> production. Memorizing targeted phrases allows test-takers to confidently introduce topics and express personal opinions without hesitation during the conversational segment.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Practising the Phrases Under Exam Conditions
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        A phrase list helps most when you can recall it under time pressure. Learn the phrases from the tables below
        or the free PDF, then use them in a timed answer. Logami is built by German teachers and has 12 complete Goethe B1 mock exams
        in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that
        brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          For the complete B1 Redemittel collection.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full Goethe B1 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="goethe"
        level="b1"
        examName="Goethe B1"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> October 7, 2026
      </p>
      <SprechenSamplesCallout provider="goethe" level="b1" />
      <RelatedResources provider="goethe" level="b1" variant="redemittel" />

    </article>
  );
};

export default GoetheB1RedemittelPost;
